/* Knoxlabs homepage refresh: hero art spring, sliding explorer tabs, sticky tab bar without page jumps, accordion motion, logo marquee. */
(() => {
  if (window.__knoxHomeRefresh) return;
  window.__knoxHomeRefresh = true;
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Hero art follows the mouse on independent X/Y springs (damping 1.0, response 0.4s).
     The spring retargets from its live value and velocity, so direction changes never jump. */
  const springs = new Map();
  const springTo = (el, tx, ty) => {
    let s = springs.get(el);
    if (!s) { s = { x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0, raf: 0, last: 0 }; springs.set(el, s); }
    s.tx = tx; s.ty = ty;
    if (s.raf) return;
    const w = 2 * Math.PI / 0.4;
    const put = () => { el.style.setProperty('--kx', s.x.toFixed(2) + 'px'); el.style.setProperty('--ky', s.y.toFixed(2) + 'px'); };
    const step = t => {
      const dt = Math.min(0.032, s.last ? (t - s.last) / 1000 : 1 / 60); s.last = t;
      s.vx += (-w * w * (s.x - s.tx) - 2 * w * s.vx) * dt;
      s.vy += (-w * w * (s.y - s.ty) - 2 * w * s.vy) * dt;
      s.x += s.vx * dt; s.y += s.vy * dt;
      if (Math.abs(s.x - s.tx) < .05 && Math.abs(s.y - s.ty) < .05 && Math.abs(s.vx) < .05 && Math.abs(s.vy) < .05) {
        s.x = s.tx; s.y = s.ty; s.vx = 0; s.vy = 0; put(); s.raf = 0; s.last = 0; return;
      }
      put(); s.raf = requestAnimationFrame(step);
    };
    s.raf = requestAnimationFrame(step);
  };
  const initHero = () => {
    document.querySelectorAll('knox-paths .kn-path').forEach(panel => {
      if (panel.dataset.khSpring) return;
      panel.dataset.khSpring = 'true';
      panel.addEventListener('pointermove', e => {
        if (e.pointerType === 'touch' || reduceMotion()) return;
        const r = panel.getBoundingClientRect();
        if (!r.width || !r.height) return;
        const nx = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width - .5) * 2));
        const ny = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height - .5) * 2));
        springTo(panel, nx * 8, ny * 6);
      });
      panel.addEventListener('pointerleave', () => springTo(panel, 0, 0));
    });
  };

  /* Explorer: sliding selection pill, direction-aware panel entrance, sticky tab bar that never makes the page jump. */
  const initExplorer = () => {
    const explorer = document.querySelector('knox-home-explorer');
    if (!explorer || explorer.dataset.khRefresh) return;
    explorer.dataset.khRefresh = 'true';
    const tablist = explorer.querySelector('.khe-tabs');
    const bar = explorer.querySelector('.khe-top');
    const sentinel = explorer.querySelector('.khe-sentinel');
    const tabs = [...explorer.querySelectorAll('[data-explorer-tab]')];
    const header = document.querySelector('.site-header');
    let headerH = 0;

    const measure = () => {
      const h = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      if (h !== headerH) { headerH = h; document.body.style.setProperty('--kh-header-h', h + 'px'); }
      if (bar && sentinel) bar.dataset.stuck = String(sentinel.getBoundingClientRect().top < headerH - 1);
    };
    let frame = 0;
    const schedule = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; measure(); }); };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    measure();

    const alignPanel = () => {
      if (!sentinel) return;
      const target = Math.max(0, sentinel.getBoundingClientRect().top + window.scrollY - headerH);
      if (window.scrollY > target + 1) window.scrollTo({ top: target, behavior: 'auto' });
    };

    let index = Math.max(0, tabs.findIndex(t => t.getAttribute('aria-selected') === 'true'));
    if (tablist) tablist.style.setProperty('--ti', index);
    const sync = () => {
      const next = tabs.findIndex(t => t.getAttribute('aria-selected') === 'true');
      if (next < 0 || next === index) return;
      explorer.dataset.dir = next > index ? 'fwd' : 'back';
      explorer.dataset.switched = 'true';
      index = next;
      if (tablist) tablist.style.setProperty('--ti', index);
      alignPanel();
    };
    new MutationObserver(sync).observe(explorer, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
  };

  /* "Refine your setup" accordion: height and content animate on open and close, interruptible mid-way. */
  if (!window.__knoxHomeAccordion) {
    window.__knoxHomeAccordion = true;
    document.addEventListener('click', e => {
      const sm = e.target.closest && e.target.closest('knox-home-explorer details.khs-requirements > summary');
      if (!sm) return;
      const d = sm.parentElement;
      if (!d || reduceMotion() || !d.animate) return;
      e.preventDefault();
      const body = sm.nextElementSibling;
      const from = d.getBoundingClientRect().height;
      if (d._acc) { d._acc.cancel(); d._acc = null; }
      if (d._accBody) { d._accBody.cancel(); d._accBody = null; }
      const opening = !d.open || d.hasAttribute('data-closing');
      let to;
      d.style.overflow = 'hidden';
      if (opening) { d.removeAttribute('data-closing'); d.open = true; to = d.getBoundingClientRect().height; }
      else { d.setAttribute('data-closing', ''); to = sm.getBoundingClientRect().height + (d.offsetHeight - d.clientHeight); }
      const a = d.animate({ height: [from + 'px', to + 'px'] }, { duration: opening ? 320 : 240, easing: opening ? 'cubic-bezier(.2,.8,.2,1)' : 'cubic-bezier(.4,0,.2,1)' });
      d._acc = a;
      if (body && body.animate) d._accBody = body.animate(opening ? { opacity: [0, 1], transform: ['translateY(-6px)', 'none'] } : { opacity: [1, 0] }, { duration: opening ? 280 : 160, delay: opening ? 40 : 0, easing: 'ease-out', fill: 'both' });
      a.onfinish = () => {
        d._acc = null;
        if (!opening) { d.open = false; d.removeAttribute('data-closing'); }
        d.style.overflow = '';
        if (d._accBody) { d._accBody.cancel(); d._accBody = null; }
      };
    });
  }

  /* Customer logo marquee: pause the CSS animation while it is offscreen so it costs nothing out of view. */
  const initMarquee = () => {
    document.querySelectorAll('.kt-marquee').forEach(mq => {
      if (mq.dataset.khObserved || !('IntersectionObserver' in window)) return;
      mq.dataset.khObserved = 'true';
      new IntersectionObserver(entries => entries.forEach(en => mq.toggleAttribute('data-paused', !en.isIntersecting))).observe(mq);
    });
  };

  const init = () => { initHero(); initExplorer(); initMarquee(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();